// Live progress for the "Connecting your Bridge" screen (ENG-3796).
//
// Read-only: three independent signals the app already publishes, mapped to the
// three rows the indicator draws. Nothing here starts, retries, or times out a
// connection — it reports what is already true. The timeout and failure
// handling are a separate concern and land with the screen that needs them.

import { broker } from '#~/agent/broker.js';
import { useAgents } from '#~/agent/hooks.js';
// Both room signals, deliberately. useLinkedRoom asks the cloud; useLastRoomIdPref
// is the saved id the app itself navigates by. See roomLinked below.
import { useLastRoomIdPref, useLinkedRoom } from '#~/room/hook_last_id.js';
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import type { BridgeConnectionSteps } from '../types.js';

/**
 * Resolve the three bridge-connection signals.
 *
 * Each is deliberately separate rather than derived from one "ready" flag,
 * because they genuinely land out of order:
 *
 * - `agentDetected` — an agent record exists in depot. The earliest observable
 *   sign that the bridge half of AutoLink has started, and it can precede the
 *   proxy being bootstrapped.
 * - `bridgeAuthenticated` — `broker.acquire()` returns a proxy for at least one
 *   known agent. A record in depot does not imply this.
 * - `roomLinked` — a room id resolved, from EITHER source. `useLinkedRoom` runs
 *   its own cloud lookup (token, then listRooms, then match this agent), and that
 *   lookup cannot start until the identity store settles — which for an early
 *   "unlinked" answer waits up to 30s, the same budget as this screen's whole
 *   ceiling. Meanwhile the app has already navigated into the studio using the
 *   SAVED room id, so the user can be sitting in a linked studio, camera on
 *   screen, while this row still says "Linking your Studio…" and then fails
 *   (reported live 2026-08-25). Accepting the saved id as well means the row
 *   cannot contradict the studio the user is already looking at.
 *
 * Collapsing them into a single boolean would make the indicator jump from
 * nothing to everything, which is exactly the progress it exists to show.
 */
export const useBridgeSteps = (): BridgeConnectionSteps => {
  const agents = useAgents((µ) => µ);
  const { roomId: linkedRoomId } = useLinkedRoom();
  const [savedRoomId] = useLastRoomIdPref();

  const readBridgeAuthenticated = useCallback(
    () => agents.some((agent) => broker.acquire(agent.id) !== undefined),
    [agents]
  );
  // The proxy can arrive after the agent list stabilizes, so agent changes alone
  // cannot wake this check. The broker publishes that availability boundary.
  const bridgeAuthenticated = useSyncExternalStore(
    broker.subscribe,
    readBridgeAuthenticated,
    readBridgeAuthenticated
  );

  const agentDetected = agents.length > 0;
  const roomLinked = linkedRoomId !== undefined || savedRoomId !== undefined;

  return useMemo(
    () => ({ agentDetected, bridgeAuthenticated, roomLinked }),
    [agentDetected, bridgeAuthenticated, roomLinked]
  );
};
